import { makeAutoObservable } from "mobx";
import FirebaseService from "./FirebaseService";
import { collection, doc, setDoc, getDoc, getDocs, query, where, orderBy, deleteDoc } from "firebase/firestore";
import { NutritionProductPlan } from "./PlansService";

export type TrainingRun = {
    id: string;
    name: string;
    date: Date;
    distance: number; // in km
    time: number; // in minutes
    elevationGain: number; // in meters
    elevationLoss: number; // in meters
    nutrition: NutritionProductPlan[];
    performance: number; // 0-10 slider (felt weak to felt strong)
    gutIssues: number; // 0-10 slider (no issues to strong gut issues)
    temperature: number; // 0-10 slider (very cold to very warm)
    notes: string;
    source: "timeontrails" | "strava" | "manual";
    userId: string;
    createdAt: Date;
    modifiedAt: Date;
};

export default class TrainingRunsService {
    private _trainingRuns: TrainingRun[] = [];
    private _isLoadingTrainingRuns: boolean = false;
    private _currentTrainingRun: TrainingRun | null = null;
    private _isSaving: boolean = false;
    private _hasSaveError: boolean = false;

    constructor(private firebaseService: FirebaseService) {
        makeAutoObservable(this, undefined, { autoBind: true, deep: false });
    }

    get trainingRuns() {
        return this._trainingRuns;
    }

    get isLoadingTrainingRuns() {
        return this._isLoadingTrainingRuns;
    }

    get currentTrainingRun() {
        return this._currentTrainingRun;
    }

    get isSaving() {
        return this._isSaving;
    }

    get hasSaveError() {
        return this._hasSaveError;
    }

    async loadTrainingRuns(userId: string, startDate: Date, endDate: Date) {
        this._isLoadingTrainingRuns = true;
        try {
            const q = query(
                collection(this.firebaseService.db, "trainingRuns"),
                where("userId", "==", userId),
                where("date", ">=", startDate),
                where("date", "<=", endDate),
                orderBy("date", "desc")
            );
            const querySnapshot = await getDocs(q);
            this._trainingRuns = querySnapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
                date: doc.data().date?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                modifiedAt: doc.data().modifiedAt?.toDate(),
            } as TrainingRun));
        } catch (error) {
            console.error("Error loading training runs:", error);
        } finally {
            this._isLoadingTrainingRuns = false;
        }
    }

    async saveTrainingRun(trainingRun: TrainingRun) {
        this._isSaving = true;
        this._hasSaveError = false;
        try {
            const saved = { ...trainingRun, modifiedAt: new Date() };
            const trainingRunRef = doc(this.firebaseService.db, "trainingRuns", trainingRun.id);
            await setDoc(trainingRunRef, saved);
            this._trainingRuns = this._trainingRuns.map(tr =>
                tr.id === saved.id ? saved : tr
            );
            this._isSaving = false;
            return saved;
        } catch (error) {
            console.error("Error saving training run:", error);
            this._isSaving = false;
            this._hasSaveError = true;
            throw error;
        }
    }

    async loadTrainingRun(trainingRunId: string) {
        try {
            const trainingRunRef = doc(this.firebaseService.db, "trainingRuns", trainingRunId);
            const trainingRunDoc = await getDoc(trainingRunRef);
            if (trainingRunDoc.exists()) {
                this._currentTrainingRun = {
                    ...trainingRunDoc.data(),
                    id: trainingRunDoc.id,
                    date: trainingRunDoc.data().date?.toDate(),
                    createdAt: trainingRunDoc.data().createdAt?.toDate(),
                    modifiedAt: trainingRunDoc.data().modifiedAt?.toDate(),
                } as TrainingRun;
                return this._currentTrainingRun;
            }
            return null;
        } catch (error) {
            console.error("Error loading training run:", error);
            return null;
        }
    }

    async deleteTrainingRun(trainingRunId: string) {
        try {
            const trainingRunRef = doc(this.firebaseService.db, "trainingRuns", trainingRunId);
            await deleteDoc(trainingRunRef);
            this._trainingRuns = this._trainingRuns.filter(tr => tr.id !== trainingRunId);
        } catch (error) {
            console.error("Error deleting training run:", error);
            throw error;
        }
    }

    clearCurrentTrainingRun() {
        this._currentTrainingRun = null;
    }
}
